import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const AREAS = ['Shower', 'Basin', 'EWC', 'Kitchen', 'Faucet'];
const AREA_MAP = {
  'S': 'Shower',
  'B': 'Basin',
  'E': 'EWC',
  'K': 'Kitchen',
  'F': 'Faucet'
};
const SELECTION_NAMES = Array.from({ length: 15 }, (_, i) => `selection ${i + 1}`);

const KEY_MAP = {
  'sku_code': 'productCode',
  'sku code': 'productCode',
  'qty': 'quantity',
  'area': 'area',
  'discount price': 'discountPrice',
  'discount %': 'discountPercent',
  'discount%': 'discountPercent',
  'discount percent': 'discountPercent',
};

export default function ExcelImportModal({ isOpen, onClose, onImport }) {
  const { masterProduct = [] } = useSelector((state) => state.product);
  const [summary, setSummary] = useState(null);  // { totalRows, totalSkipped, selections: [{name, count}] }
  const [payload, setPayload] = useState([]);     // [{ selectionIndex, rows }]
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef();

  const handleDownload = (e) => {
    e.preventDefault();
    const a = document.createElement('a');
    a.href = 'https://quotationsapis.groheapp.in/public/excel/sku_data.xlsx';
    a.download = 'sku_data.xlsx';
    a.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setSummary(null);
    setPayload([]);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (ev) => {
      setTimeout(() => {
        try {
          // Build SKU lookup
          const productMap = {};
          masterProduct.forEach(p => {
            if (p.sku_code) productMap[String(p.sku_code).trim().toUpperCase()] = p;
          });

          const wb = XLSX.read(ev.target.result, { type: 'array' });
          const result = [];
          const allErrors = [];
          let totalSkipped = 0;

          wb.SheetNames.forEach(sheetName => {
            const selIdx = SELECTION_NAMES.findIndex(
              n => n.toLowerCase() === sheetName.trim().toLowerCase()
            );
            if (selIdx === -1) return;

            const ws = wb.Sheets[sheetName];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            if (raw.length < 2) return;

            const headers = raw[0].map(h => String(h).toLowerCase().trim());
            const rows = [];

            raw.slice(1).forEach((row, ri) => {
              if (row.every(c => c === '' || c === null || c === undefined)) return;

              const obj = {};
              headers.forEach((h, ci) => {
                const key = KEY_MAP[h];
                if (key !== undefined) obj[key] = row[ci];
              });

              if (!obj.productCode) { totalSkipped++; return; }

              const master = productMap[String(obj.productCode).trim().toUpperCase()];
              if (!master) {
                totalSkipped++;
                allErrors.push(`SKU "${obj.productCode}" not found (${sheetName}, row ${ri + 2})`);
                return;
              }

              const rawAreaInput = String(obj.area || '').trim().toUpperCase();
              let finalArea = '';

              if (rawAreaInput) {
                // Check shortcode map first, then fallback to full name check
                finalArea = AREA_MAP[rawAreaInput] || AREAS.find(a => a.toUpperCase() === rawAreaInput) || '';

                if (!finalArea) {
                  totalSkipped++;
                  allErrors.push(`Invalid area code "${rawAreaInput}" (${sheetName}, row ${ri + 2})`);
                  return;
                }
              }

              rows.push({
                // from catalog
                productCode: master.sku_code,
                brand: master.brand || '',
                description: master.product_desc || '',
                collection: master.collection || '',
                productType: master.product_typ_group || '',
                category: master.category || '',
                priceSegment: master.priceSegment || '',
                finish: master.finish || '',
                image: master.image || null,
                mrp: Number(master.mrp) || 0,
                basePrice: Number(master.basePrice) || 0,
                gstRate: Number(master.gst) || 18,
                stock: Number(master.stockAvailable) || 0,
                stockIndicator: master.stockIndicator || '',
                // from excel
                quantity: Math.max(1, Number(obj.quantity) || 1),
                area: finalArea,
                discountPercent: Number(obj.discountPercent) || 0,
                discountPrice: Number(obj.discountPrice) || 0,
              });
            });

            if (rows.length > 0) {
              result.push({ selectionIndex: selIdx, selectionName: sheetName, rows });
            }
          });

          setPayload(result);
          setErrors(allErrors);
          setSummary({
            totalRows: result.reduce((s, r) => s + r.rows.length, 0),
            totalSkipped,
            selections: result.map(r => ({ name: r.selectionName, count: r.rows.length })),
          });
        } catch (err) {
          setErrors([`Could not parse file: ${err.message}`]);
        } finally {
          setParsing(false);
        }
      }, 50);
    };

    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = () => {
    onImport(payload);
    handleClose();
  };

  const handleClose = () => {
    setSummary(null); setPayload([]); setErrors([]);
    setFileName(''); setParsing(false);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      keepMounted={false}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700} fontSize={15}>Import from Excel</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Download */}
        <Box display="flex" alignItems="center" justifyContent="space-between"
          p={1.5} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
          <Box>
            <Typography variant="body2" fontWeight={600} fontSize={12}>Download Template</Typography>
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              15 sheets · sku_code, qty, area, discount Price, discount %
            </Typography>
          </Box>
          <Button size="small" variant="outlined" color="success" onClick={handleDownload}
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
            .xlsx
          </Button>
        </Box>

        {/* Upload */}
        <Box display="flex" alignItems="center" justifyContent="space-between"
          p={1.5} bgcolor="grey.50" borderRadius={2} border="1px solid" borderColor="grey.200">
          <Box minWidth={0} flex={1} mr={1}>
            <Typography variant="body2" fontWeight={600} fontSize={12}>Upload Filled File</Typography>
            <Typography variant="caption" color="text.secondary" fontSize={10} noWrap>
              {fileName || 'No file chosen'}
            </Typography>
          </Box>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
          <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()}
            disabled={parsing}
            startIcon={parsing
              ? <CircularProgress size={12} />
              : <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
            {parsing ? 'Reading…' : 'Upload'}
          </Button>
        </Box>

        {/* Errors */}
        {errors.length > 0 && (
          <Alert severity="warning" sx={{ py: 0.5, fontSize: 11 }}>
            <Typography fontWeight={700} fontSize={11} mb={0.5}>
              {errors.length} row{errors.length !== 1 ? 's' : ''} skipped
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 80, overflowY: 'auto' }}>
              {errors.map((e, i) => <li key={i} style={{ fontSize: 10 }}>{e}</li>)}
            </Box>
          </Alert>
        )}

        {/* Confirmation summary — the ONLY data shown after upload */}
        {summary && summary.totalRows > 0 && (
          <Box p={2} bgcolor="#f0fdf4" borderRadius={2} border="1px solid #bbf7d0">
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 18 }} />
              <Typography fontWeight={700} fontSize={13} color="#15803d">
                Ready to import
              </Typography>
            </Box>

            {/* Total count */}
            <Box display="flex" justifyContent="space-between" alignItems="center"
              pb={1} mb={1} borderBottom="1px solid #bbf7d0">
              <Typography fontSize={12} color="text.secondary">Total products</Typography>
              <Typography fontSize={13} fontWeight={800} color="#15803d">
                {summary.totalRows}
              </Typography>
            </Box>

            {/* Per-selection breakdown */}
            <Box display="flex" flexDirection="column" gap={0.5}>
              {summary.selections.map(sel => (
                <Box key={sel.name} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography fontSize={11} color="text.secondary" textTransform="capitalize">
                    {sel.name}
                  </Typography>
                  <Typography fontSize={11} fontWeight={700} color="#334155">
                    {sel.count} product{sel.count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Skipped */}
            {summary.totalSkipped > 0 && (
              <Typography fontSize={10} color="warning.main" mt={1}>
                {summary.totalSkipped} row{summary.totalSkipped !== 1 ? 's' : ''} skipped (SKU not found or invalid)
              </Typography>
            )}
          </Box>
        )}

        {summary && summary.totalRows === 0 && !parsing && (
          <Alert severity="error" sx={{ fontSize: 11 }}>
            No valid products found. Check that SKU codes exist in the catalog.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={handleClose} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={handleImport}
          disabled={!summary || summary.totalRows === 0}
          variant="contained" color="success" size="small"
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Import {summary?.totalRows ? `${summary.totalRows} Products` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}